import React, { useState, useEffect } from 'react';
import { Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PhoneInput from './PhoneInput';
import OTPInput from './OTPInput';
import ProfileComplete from './ProfileComplete';
import {
  sendOTP,
  resetSendOTP,
  verifyOTP,
  resetVerifyOTP,
  completeProfile,
  resetCompleteProfile,
  userDetailsFromToken
} from '../../actions';
import { parsePhoneAuthError } from '../../utils/phoneValidation';
import { trackPhoneAuthStage } from '../../utils/tracking';
import { clearConsumedReferralParam, getSafeNextPath } from '../../utils/attribution';
import { getActiveReferralCode, clearReferralCode } from '../../utils/referral';
import mixpanel from 'mixpanel-browser';
import Hotjar from '@hotjar/browser';

const AUTH_STATES = {
  PHONE_INPUT: 'PHONE_INPUT',
  OTP_VERIFY: 'OTP_VERIFY',
  PROFILE_COMPLETE: 'PROFILE_COMPLETE'
};
const POST_LOGIN_PATHS = ['/daily-absite-question', '/create-quiz', '/absite-diagnostic', '/quiz-history'];

const PhoneAuthSection = () => {
  const [authState, setAuthState] = useState(AUTH_STATES.PHONE_INPUT);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Redux state selectors
  const sendOTPState = useSelector((state) => state.sendOTP);
  const verifyOTPState = useSelector((state) => state.verifyOTP);
  const completeProfileState = useSelector((state) => state.completeProfile);
  const userDetailsFromTokenRes = useSelector((state) => state.userDetailsFromToken?.userDetails);

  // Initialize and handle page reload with incomplete profile
  useEffect(() => {
    const token = window.localStorage.getItem('token');
    const isProfileComplete = window.localStorage.getItem('isProfileComplete');

    // If user reloaded page during phone auth flow with incomplete profile
    if (token && isProfileComplete === 'false') {
      // User has token but profile is incomplete - show profile completion
      setAuthState(AUTH_STATES.PROFILE_COMPLETE);
    } else {
      // Track when phone auth is started (fresh session)
      trackPhoneAuthStage.started();
    }
  }, []);

  // Handle Send OTP success
  useEffect(() => {
    if (sendOTPState?.success && authState === AUTH_STATES.PHONE_INPUT) {
      setAuthState(AUTH_STATES.OTP_VERIFY);
      trackPhoneAuthStage.otpSent(currentPhoneNumber, sendOTPState.isNewUser);
    }
  }, [sendOTPState, authState, currentPhoneNumber]);

  // Handle Send OTP error
  useEffect(() => {
    if (sendOTPState?.error) {
      trackPhoneAuthStage.otpFailed(sendOTPState.error);
    }
  }, [sendOTPState?.error]);

  // Handle Verify OTP success
  useEffect(() => {
    if (verifyOTPState?.user && authState === AUTH_STATES.OTP_VERIFY) {
      const { accessToken, id, phoneNumber, isProfileComplete } = verifyOTPState.user;

      // Store token immediately
      window.localStorage.setItem('token', accessToken);
      window.localStorage.setItem('userId', id);
      clearReferralCode(window.localStorage);
      clearConsumedReferralParam();

      // Store profile completion status for reload handling
      window.localStorage.setItem('isProfileComplete', isProfileComplete ? 'true' : 'false');

      // Track OTP verification success
      trackPhoneAuthStage.otpVerified(verifyOTPState.user);

      // Check if profile completion is needed (rely only on isProfileComplete, not isNewUser)
      if (!isProfileComplete) {
        // Profile incomplete - show profile completion screen
        setAuthState(AUTH_STATES.PROFILE_COMPLETE);
      } else {
        // Profile already complete - fetch full user details and redirect
        window.localStorage.setItem('isProfileComplete', 'true');
        dispatch(userDetailsFromToken());
      }
    }
  }, [verifyOTPState, authState, dispatch]);

  // Handle Verify OTP error
  useEffect(() => {
    if (verifyOTPState?.error) {
      trackPhoneAuthStage.otpFailed(verifyOTPState.error);
    }
  }, [verifyOTPState?.error]);

  // Handle Complete Profile success
  useEffect(() => {
    if (completeProfileState?.success && authState === AUTH_STATES.PROFILE_COMPLETE) {
      // Track profile completion
      trackPhoneAuthStage.profileCompleted({ id: window.localStorage.getItem('userId') });

      // Mark profile as complete
      window.localStorage.setItem('isProfileComplete', 'true');

      // Profile completed, fetch full user details
      dispatch(userDetailsFromToken());
    }
  }, [completeProfileState, authState, dispatch]);

  // Handle userDetailsFromToken success (final step)
  useEffect(() => {
    if (userDetailsFromTokenRes?.id) {
      // Check if profile is complete before redirecting
      const isProfileComplete = window.localStorage.getItem('isProfileComplete');

      // If profile is incomplete, don't redirect - user should complete profile first
      if (isProfileComplete === 'false') {
        console.log('Profile incomplete, staying on profile completion screen');
        return;
      }

      // Identify user in active analytics platforms
      mixpanel.track('Sign In', {
        $name: userDetailsFromTokenRes.displayName,
        $user_id: userDetailsFromTokenRes.id,
        email: userDetailsFromTokenRes.email,
        role: userDetailsFromTokenRes.primaryRole,
        method: 'phone_otp'
      });
      mixpanel.identify(userDetailsFromTokenRes.id);
      mixpanel.people.set({
        $email: userDetailsFromTokenRes.email,
        $name: userDetailsFromTokenRes.displayName,
        $phone: currentPhoneNumber,
        $role: userDetailsFromTokenRes.primaryRole
      });

      // Initialize Hotjar
      const siteId = process.env.REACT_APP_HOTJAR_KEY;
      const hotjarVersion = process.env.REACT_APP_HOTJAR_VERSION;
      if (siteId && hotjarVersion) {
        Hotjar.init(siteId, hotjarVersion);
      }

      // Store additional user data
      const userData = {
        name: userDetailsFromTokenRes.displayName,
        email: userDetailsFromTokenRes.email
      };
      window.localStorage.setItem('userdetails', JSON.stringify(userData));
      window.localStorage.setItem('role', userDetailsFromTokenRes.primaryRole);

      // Redirect only if profile is complete.
      history.push(
        getSafeNextPath(window.location.search, {
          allowedPathnames: POST_LOGIN_PATHS,
        }) || '/create-quiz'
      );
    }
  }, [userDetailsFromTokenRes, history, currentPhoneNumber, location.search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetSendOTP());
      dispatch(resetVerifyOTP());
      dispatch(resetCompleteProfile());
    };
  }, [dispatch]);

  // Handlers
  const handleSendOTP = (data) => {
    setCurrentPhoneNumber(data.phoneNumber);
    dispatch(sendOTP(data));
  };

  const handleVerifyOTP = (data) => {
    const referralCode = getActiveReferralCode(window.localStorage);
    dispatch(verifyOTP({ ...data, ...(referralCode && { referralCode }) }));
  };

  const handleResendOTP = (data) => {
    trackPhoneAuthStage.otpResent(data.phoneNumber);
    dispatch(resetSendOTP());
    dispatch(resetVerifyOTP());
    dispatch(sendOTP(data));
  };

  const handleCompleteProfile = (data) => {
    dispatch(completeProfile(data));
  };

  // Render current auth screen based on state
  const renderAuthScreen = () => {
    switch (authState) {
      case AUTH_STATES.PHONE_INPUT:
        return (
          <PhoneInput
            onSendOTP={handleSendOTP}
            loading={sendOTPState?.loading}
            error={sendOTPState?.error ? parsePhoneAuthError(sendOTPState.error) : null}
            cooldownUntil={sendOTPState?.cooldownUntil}
          />
        );

      case AUTH_STATES.OTP_VERIFY:
        return (
          <OTPInput
            phoneNumber={currentPhoneNumber}
            onVerifyOTP={handleVerifyOTP}
            onResendOTP={handleResendOTP}
            loading={verifyOTPState?.loading}
            error={verifyOTPState?.error ? parsePhoneAuthError(verifyOTPState.error) : null}
            remainingAttempts={verifyOTPState?.remainingAttempts}
            resendCooldown={60}
          />
        );

      case AUTH_STATES.PROFILE_COMPLETE:
        return (
          <ProfileComplete
            onCompleteProfile={handleCompleteProfile}
            loading={completeProfileState?.loading}
            error={completeProfileState?.error ? parsePhoneAuthError(completeProfileState.error) : null}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box className="phone-auth-section">
      {renderAuthScreen()}
    </Box>
  );
};

export default PhoneAuthSection;
